import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { fetchTaskById } from "../store/actions/taskActions";
import { ArrowLeft, Circle, Timer, CheckCircle2, Calendar, User, Pencil } from "lucide-react";

const Title = styled.h2`
  margin: 4px 0 0;
  font-size: 24px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.heading};
`;

const Muted = styled.p`
  margin: 2px 0 0;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.body};
`;

const priorityColors = {
  low: { bg: "#dbeafe", text: "#1e40af" },
  medium: { bg: "#fef3c7", text: "#92400e" },
  high: { bg: "#fee2e2", text: "#991b1b" },
};

const statusIcons = {
  todo: Circle,
  "in-progress": Timer,
  completed: CheckCircle2,
};

export default function TaskDetails() {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { task, loading, error } = useSelector((state) => state.task);

  useEffect(() => {
    if (taskId) dispatch(fetchTaskById(taskId));
  }, [dispatch, taskId]);

  if (loading && !task) {
    return (
      <div className="animate-fadeIn">
        <Button $variant="ghost" $size="sm" onClick={() => navigate("/tasks")}><ArrowLeft size={16} /> Back to Tasks</Button>
        <p style={{ color: "#6b7280", marginTop: 16 }}>Loading...</p>
      </div>
    );
  }
  if (error && !task) {
    return (
      <div className="animate-fadeIn">
        <Button $variant="ghost" $size="sm" onClick={() => navigate("/tasks")}><ArrowLeft size={16} /> Back to Tasks</Button>
        <p style={{ color: "#dc2626", marginTop: 16 }}>{error}</p>
      </div>
    );
  }
  if (!task) {
    return (
      <div className="animate-fadeIn">
        <Button $variant="ghost" $size="sm" onClick={() => navigate("/tasks")}><ArrowLeft size={16} /> Back to Tasks</Button>
        <p style={{ color: "#6b7280", marginTop: 16 }}>Task not found.</p>
      </div>
    );
  }

  const StatusIcon = statusIcons[task.status];
  const pc = priorityColors[task.priority] || priorityColors.medium;
  const isOverdue = task.status !== "completed" && task.dueDate && new Date(task.dueDate) < new Date();

  return (
    <div className="animate-fadeIn space-y-6">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 16 }}>
        <div>
          <Button $variant="ghost" $size="sm" onClick={() => navigate("/tasks")}><ArrowLeft size={16} /> Back to Tasks</Button>
          <Title>{task.title}</Title>
          <Muted>Created {task.createdAt ? new Date(task.createdAt).toLocaleDateString() : "—"}</Muted>
        </div>
        <Button $variant="outline" $size="sm" onClick={() => navigate("/tasks", { state: { editTaskId: task._id } })}>
          <Pencil size={16} style={{ marginRight: 8 }} /> Edit
        </Button>
      </div>

      <Card style={{ padding: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 8, background: "#f3f4f6", fontSize: 14 }}>
            <StatusIcon size={16} /> {task.status}
          </span>
          <span style={{ padding: "6px 12px", borderRadius: 8, fontSize: 14, fontWeight: 500, background: pc.bg, color: pc.text }}>{task.priority}</span>
          {isOverdue && <span style={{ fontSize: 14, color: "#ef4444", fontWeight: 600 }}>Overdue</span>}
        </div>
        {task.description && <p style={{ margin: "0 0 16px", fontSize: 15, lineHeight: 1.6, color: "#374151" }}>{task.description}</p>}
        <div style={{ display: "grid", gap: 8, fontSize: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Calendar size={16} style={{ color: "#6b7280" }} />
            <span style={{ color: "#6b7280" }}>Due:</span>
            <span>{task.dueDate ? new Date(task.dueDate).toLocaleString() : "No date"}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <User size={16} style={{ color: "#6b7280" }} />
            <span style={{ color: "#6b7280" }}>Assignee:</span>
            <span>{task.assigneeId?.fullName || task.assigneeId?.email || "Unassigned"}</span>
          </div>
          {task.tags?.length > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ color: "#6b7280" }}>Tags:</span>
              {task.tags.map((t) => (
                <span key={t} style={{ padding: "4px 8px", borderRadius: 6, fontSize: 12, background: "#f3f4f6" }}>{t}</span>
              ))}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
